import React, { useEffect, useRef, useState } from 'react';
import { Send, X, UserPlus, Bot, BookOpen } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ChatLeadForm from './ChatLeadForm';
import { useChat } from './useChat';
import { trackClick } from '../landing/constants';

const WHATSAPP_NUMBER = '584242227314';
const SUGGESTIONS = ['¿Cuál es el más económico?', 'Viajo con mis padres mayores de 70', '¿Cómo funciona el código WELCOME10?'];
const ARTICLE_SUGGESTIONS = ['Resúmeme este artículo en 3 puntos', '¿Qué aliado me conviene según este artículo?'];
const DESTINOS = [
  { label: 'Europa', emoji: '🇪🇺', text: 'Viajo a Europa (Schengen). ¿Qué seguro necesito y cuál me recomiendas?' },
  { label: 'USA', emoji: '🗽', text: 'Viajo a Estados Unidos. ¿Qué seguro de viaje me recomiendas?' },
  { label: 'Venezuela', emoji: '🇻🇪', text: 'Viajo dentro de Venezuela. ¿Qué aliado me cubre?' },
  { label: 'Latinoamérica', emoji: '🌎', text: 'Viajo por Latinoamérica y el Caribe. ¿Cuál me conviene?' },
];

export const buildWhatsAppSummary = (messages) => {
  const userMsgs = messages.filter((m) => m.role === 'user').map((m) => m.content.slice(0, 120));
  const partners = [...new Set(messages.flatMap((m) => m.partners || []))];
  let text = 'Hola, vengo del asistente virtual de IwannaInsurance.';
  if (userMsgs.length) text += `\n\nMi consulta:\n${userMsgs.slice(-4).map((m) => `- ${m}`).join('\n')}`;
  if (partners.length) text += `\n\nAliado sugerido: ${partners.slice(0, 2).join(' / ')}`;
  text += '\n\n¿Me ayudan a cotizar?';
  return text.slice(0, 900);
};

const WhatsAppIcon = () => (
  <svg viewBox="0 0 32 32" className="h-4 w-4 fill-current" aria-hidden="true"><path d="M16 3C9.4 3 4 8.3 4 14.9c0 2.3.7 4.5 1.9 6.4L4 29l7.9-2c1.3.5 2.7.7 4.1.7 6.6 0 12-5.3 12-11.9S22.6 3 16 3zm0 21.8c-1.3 0-2.6-.3-3.7-.8l-.3-.1-4.1 1.1 1.1-3.9-.2-.3c-1.1-1.6-1.7-3.5-1.7-5.4C7.1 9.6 11.1 5.7 16 5.7s8.9 3.9 8.9 8.7c0 4.8-4 8.7-8.9 8.7z" /></svg>
);

export const ChatWindow = ({ config, article, onClose }) => {
  const { messages, send, sending, sessionId } = useChat(config.welcome);
  const [input, setInput] = useState('');
  const [showLead, setShowLead] = useState(false);
  const bottomRef = useRef(null);
  const partnerLinks = Object.fromEntries(config.partners.map((p) => [p.nombre, p.link]));
  const ask = (text) => send(text, article?.slug);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, showLead]);

  const submit = (e) => {
    e.preventDefault();
    ask(input);
    setInput('');
  };

  const openPartner = (name, link) => {
    trackClick(name);
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const openWhatsApp = () => {
    trackClick('WhatsApp');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildWhatsAppSummary(messages))}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid="chat-window"
      className="fixed z-50 bottom-24 right-4 left-4 sm:left-auto sm:right-6 sm:w-[380px] h-[min(560px,calc(100dvh-8rem))] flex flex-col rounded-3xl bg-white shadow-2xl shadow-slate-900/30 border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <header className="flex items-center justify-between px-4 py-3 bg-slate-950 text-white">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-600 to-red-500 flex items-center justify-center"><Bot className="h-5 w-5" /></span>
          <div className="leading-tight">
            <p className="font-bold text-sm"><span className="text-blue-400">IWANNA</span> <span className="text-red-400 font-medium">insurance</span></p>
            <p className="text-[11px] text-slate-400">Asistente virtual · responde al instante</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar chat" data-testid="chat-close-btn" className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"><X className="h-4 w-4" /></button>
      </header>
      {article && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-800" data-testid="chat-article-context">
          <BookOpen className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Leyendo: <strong>{article.titulo}</strong></span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-slate-50 to-white" data-testid="chat-messages">
        {messages.map((m) => <ChatMessage key={m.id} message={m} partnerLinks={partnerLinks} onPartnerClick={openPartner} />)}
        {messages.length <= 1 && (
          <div className="space-y-2 pt-1">
            {article && (
              <div className="flex flex-wrap gap-2" data-testid="chat-article-suggestions">
                {ARTICLE_SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => ask(s)} data-testid="chat-article-suggestion-btn" className="text-xs rounded-full bg-blue-600 text-white px-3 py-1.5 hover:bg-blue-500 transition-colors">{s}</button>
                ))}
              </div>
            )}
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">¿A dónde viajas?</p>
            <div className="grid grid-cols-2 gap-2" data-testid="chat-destino-quick">
              {DESTINOS.map((d) => (
                <button key={d.label} type="button" onClick={() => ask(d.text)} data-testid={`chat-destino-${d.label.toLowerCase()}`} className="h-11 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm font-medium flex items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 hover:-translate-y-0.5 transition-[transform,background-color,border-color] duration-200">
                  <span aria-hidden="true">{d.emoji}</span> {d.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="chat-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => ask(s)} data-testid="chat-suggestion-btn" className="text-xs rounded-full border border-blue-200 bg-white text-blue-700 px-3 py-1.5 hover:bg-blue-50 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {showLead && <ChatLeadForm sessionId={sessionId} onClose={() => setShowLead(false)} />}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-slate-100 p-3 space-y-2 bg-white">
        {messages.length > 1 && (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {!showLead && (
              <button type="button" onClick={() => setShowLead(true)} data-testid="chat-open-lead-btn" className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-500 transition-colors">
                <UserPlus className="h-3.5 w-3.5" /> Que un asesor me contacte
              </button>
            )}
            <button type="button" onClick={openWhatsApp} data-testid="chat-whatsapp-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1ebe5d] rounded-full px-3 py-1.5 transition-colors ml-auto">
              <WhatsAppIcon /> Seguir por WhatsApp
            </button>
          </div>
        )}
        <form onSubmit={submit} className="flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu pregunta..."
            maxLength={600}
            className="flex-1 h-11 rounded-full border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          />
          <button type="submit" disabled={sending || !input.trim()} aria-label="Enviar" data-testid="chat-send-btn" className="h-11 w-11 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-500 disabled:opacity-40 transition-colors">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;
